"use client";

import { useActionState, useRef } from "react";

import Form from "next/form";

import { SaleIcon } from "@/components/icons/sale.icon";
import { RichText } from "@/components/richText/richText.component";
import { Text } from "@/components/text/text.component";

import {
  CategoryProducts,
  getAccessories,
  type ProductAccessoriesActionState,
} from "@/app/blu/shop/actions";
import { DEFAULT_PDP_CATEGORY } from "@/app/blu/shop/constants";
import { useProductSearchEvent } from "@/hooks/useProductSearchEvent";
import {
  GetKitPageAccessoriesQueryResult,
  GetProductQueryResult,
  RichText as RichTextType,
} from "@/sanity.types";

import { ProductAccessoriesFilter } from "./productAccessoriesFilter.component";
import { ProductAccessoriesLoader } from "./productAccessoriesLoader.component";
import { ProductAccessoriesNotFound } from "./productAccessoriesNotFound.component";
import { ProductAccessoriesProducts } from "./productAccessoriesProducts.component";

export const ProductAccessoriesForm = ({
  products = [],
  title,
  subtitle,
  kit,
  accessories,
  basketItems,
  categoryCallout,
  selectedCategory = DEFAULT_PDP_CATEGORY,
}: ProductAccessoriesFormProps) => {
  const formRef = useRef<HTMLFormElement>(null);
  const initialResponse: ProductAccessoriesActionState = {
    products: products,
    search: "",
    sort: "recommended",
    categoryCallout,
    selectedCategory,
  };
  const [response, getAccessoriesAction, pending] = useActionState<
    ProductAccessoriesActionState,
    FormData
  >(getAccessories, initialResponse);
  useProductSearchEvent(response.search, response.products);
  const renderAccessories = () => {
    if (pending) return <ProductAccessoriesLoader />;
    if (!response.products?.length) {
      return <ProductAccessoriesNotFound response={response} />;
    }
    return (
      <ProductAccessoriesProducts
        kit={kit}
        products={response?.products}
        basketItems={basketItems}
        pending={pending}
      />
    );
  };
  const accessoriesCallout = kit?.promotion?.accessoriesCallout;
  const [firstCalloutLine, ...remainingCalloutLines] = Array.isArray(
    accessoriesCallout,
  )
    ? accessoriesCallout
    : [];
  return (
    <section id="add-to-your-system" data-testid="product-accessories">
      <div className="container flex flex-col gap-6 px-4">
        <Text
          variant="h3"
          className="text-center"
          data-testid="product-accessories-title">
          {title}
        </Text>
        <div className="text-success flex flex-col items-center text-center">
          {firstCalloutLine ? (
            <div className="flex items-center gap-2">
              <SaleIcon
                width={14}
                height={14}
                aria-hidden
                className="shrink-0"
              />
              <RichText>{[firstCalloutLine]}</RichText>
            </div>
          ) : null}
          {remainingCalloutLines.length > 0 ? (
            <RichText>{remainingCalloutLines}</RichText>
          ) : null}
          <RichText>{subtitle}</RichText>
        </div>
        <Form
          data-testid="product-accessories-form"
          action={getAccessoriesAction}
          ref={formRef}>
          <ProductAccessoriesFilter
            formRef={formRef}
            response={response}
            accessories={accessories}
          />
        </Form>
        <div className="flex min-h-125 flex-col">{renderAccessories()}</div>
      </div>
    </section>
  );
};

export type ProductAccessoriesFormProps = {
  products?: CategoryProducts | null | undefined;
  kit: GetProductQueryResult;
  accessories?: NonNullable<GetKitPageAccessoriesQueryResult>["accessories"];
  title?: string;
  subtitle?: RichTextType | null;
  categoryCallout?: RichTextType | null;
  selectedCategory: string;
  basketItems?: Record<string, number>;
};
